import React, { memo, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import useMediaQuery from "@mui/material/useMediaQuery";
import NumberFormat from "react-number-format";
import { makeStyles } from "@mui/styles";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { useSelector, useDispatch } from "react-redux";
import Button from "@mui/material/Button";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — multicotizador/actions aún no migrado a TS
import {
  continuarSolicitud,
  recotizar,
} from "../../../views/multicotizador/actions";
import { useParams, useHistory } from "react-router-dom";
import { paths, multicotizadorPath } from "~constants/referenceNames";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — multicotizador/useCurrentStep aún no migrado a TS
import { stages } from "../../../views/multicotizador/useCurrentStep";
import { calculateDate } from "~libs/dateCalculator";
import { PRENDARIOS } from "~constants/negocios";
import { Cotizacion, Compania, DetalleCotizacion } from "./types";

const useStyles = makeStyles((theme) => ({
  compania: {
    textAlign: "center",
    fontWeight: 900,
    color: theme.colours.violeta,
    paddingTop: 15,
    paddingBottom: 15,
    display: "block",
    flex: 1,
  },
  tipoPoliza: {
    textAlign: "center",
    fontWeight: 900,
    color: "#afafaf",
    textTransform: "uppercase",
  },
  importeContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "baseline",
  },
  importe: {
    textAlign: "center",
    fontWeight: 800,
    color: theme.colours.violeta,
  },
  content: {
    paddingBottom: 60,
  },
  sentence: {
    fontWeight: 600,
    lineHeight: 1.33,
    color: theme.colours.grey5,
    paddingTop: 8,
  },
  buttonContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    bottom: "0",
    justifyContent: "center",
    height: 141,
    backgroundImage: `linear-gradient(to bottom, rgba(255, 255, 255, 0), #f5f5fa 35%)`,
  },
}));

interface TipoCobertura {
  id: string;
  nombre: string;
}

interface MulticotizadorState {
  tipoCoberturaReducer: {
    tiposCobertura: TipoCobertura[];
  };
  multicotizadorReducer: {
    multicotizacionInitialStage: string;
  };
}

const useReduxSelector = () =>
  useSelector((state: MulticotizadorState) => ({
    tiposCobertura: state.tipoCoberturaReducer.tiposCobertura,
    multicotizacionInitialStage:
      state.multicotizadorReducer.multicotizacionInitialStage,
  }));

interface CotizacionDetailProps {
  isOpened: boolean;
  onClose: () => void;
  detalleCotizacion: DetalleCotizacion;
  compania: Compania;
  cotizacion: Cotizacion;
}

const CotizacionDetail: React.FC<CotizacionDetailProps> = memo(
  ({ isOpened, onClose, detalleCotizacion, compania, cotizacion }) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const history = useHistory();

    const cotizacionId = useParams<{ id: string }>().id;

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

    // `descripcion` llega nula cuando el plan no está en el esquema, que en
    // Prendarios es alcanzable porque el backend ignora `SoloEnEsquema`.
    const descripcion = cotizacion.descripcion ?? "";
    const newLinesDeclared = descripcion.includes("\n");

    const { tiposCobertura, multicotizacionInitialStage } = useReduxSelector();

    const tipoCobertura = tiposCobertura.find((tipo) => {
      return tipo.id === cotizacion.tipoPlanComercial;
    });

    const retormarSolicitud =
      new Date() < calculateDate(new Date(detalleCotizacion.fecha), 14) &&
      new Date().getMonth() === new Date(detalleCotizacion.fecha).getMonth();

    const handleContinuarSolicitud = () => {
      if (retormarSolicitud)
        dispatch(
          continuarSolicitud(cotizacionId, { ...cotizacion, ...compania })
        );
      else dispatch(recotizar(cotizacionId));
    };

    useEffect(() => {
      if (
        multicotizacionInitialStage === stages.recotizar ||
        multicotizacionInitialStage === stages.continuarSolicitud
      )
        history.push(paths[multicotizadorPath]);
    }, [history, multicotizacionInitialStage]);

    const canContinueSolicitud = detalleCotizacion.negocio !== PRENDARIOS;

    return (
      <Dialog
        open={isOpened}
        scroll="paper"
        onClose={onClose}
        fullScreen={fullScreen}
        fullWidth={true}
      >
        <DialogTitle
          style={{
            padding: 0,
          }}
        >
          <div style={{ display: "flex" }}>
            <div style={{ width: 48 }} />
            <Typography variant={"h5"} className={classes.compania}>
              {compania.compania}
            </Typography>
            <div style={{ width: 48 }}>
              <IconButton
                edge="end"
                color="inherit"
                onClick={onClose}
                aria-label="Close"
              >
                <CloseIcon />
              </IconButton>
            </div>
          </div>
          <Typography variant={"subtitle1"} className={classes.tipoPoliza}>
            {cotizacion.nombre}
          </Typography>
          <Typography variant={"subtitle1"} className={classes.tipoPoliza}>
            {tipoCobertura?.nombre}
          </Typography>
          <div className={classes.importeContainer}>
            <Typography variant={"h3"} className={classes.importe}>
              <NumberFormat
                value={cotizacion.importe}
                displayType={"text"}
                decimalScale={0}
                thousandSeparator={"."}
                decimalSeparator={","}
                prefix={"$"}
              />
            </Typography>
            <Typography variant={"subtitle1"} className={classes.tipoPoliza}>
              {"\xa0/mes"}
            </Typography>
          </div>
        </DialogTitle>
        <DialogContent className={classes.content}>
          {newLinesDeclared &&
            convertStringIntoSentencesWithNewLines(descripcion).map(
              (sentence, index) => (
                <DialogContentText
                  key={`poliza-item-sentence-${index}`}
                  className={classes.sentence}
                >
                  {sentence}
                </DialogContentText>
              )
            )}
          {!newLinesDeclared &&
            convertStringIntoSentencesWithOutNewLines(descripcion).map(
              (sentence, index) => (
                <DialogContentText
                  key={`poliza-item-sentence-${index}`}
                  className={classes.sentence}
                >
                  {sentence}
                </DialogContentText>
              )
            )}

          <DialogContentText className={classes.sentence}>
            Suma Asegurada:{" "}
            <NumberFormat
              value={cotizacion.sumaAsegurada}
              displayType={"text"}
              decimalScale={0}
              thousandSeparator={"."}
              decimalSeparator={","}
              prefix={"$"}
            />
          </DialogContentText>
        </DialogContent>
        {canContinueSolicitud && (
          <div className={classes.buttonContainer}>
            <Button
              onClick={handleContinuarSolicitud}
              variant="contained"
              color="primary"
            >
              {retormarSolicitud ? "ELEGIR ESTA PÓLIZA" : "RECOTIZAR"}
            </Button>
          </div>
        )}
      </Dialog>
    );
  }
);

export default CotizacionDetail;

const convertStringIntoSentencesWithNewLines = (
  description: string
): string[] => {
  return description.replace(/\n/g, "|").split("|");
};

const convertStringIntoSentencesWithOutNewLines = (
  description: string
): string[] => {
  return description
    .replace(/([.])\s*(?=[A-Z])/g, "$1|")
    .split("|")
    .map(convertFirstletterToUpperCase);
};

const convertFirstletterToUpperCase = (sentence: string): string =>
  sentence && sentence.length > 2
    ? sentence.charAt(0).toUpperCase() + sentence.slice(1).toLowerCase()
    : sentence;
